// V18.9.12 — Wrapper centralizado de geolocalização (web + Capacitor Android/iOS).
//
// Histórico do problema:
//  1) Usando `navigator.geolocation` no app empacotado, o prompt do SISTEMA mostrava
//     o ORIGIN do WebView ("localhost quer usar sua localização") e não persistia —
//     o usuário via o pedido a cada abertura.
//  2) Migramos para `@capacitor/geolocation` (API nativa, permissão persistente),
//     mas aí a localização simplesmente NUNCA vinha no device:
//     • `enableHighAccuracy:false` faz o plugin pedir só a permissão APROXIMADA
//       (alias `coarseLocation`) no Android 12+; depois disso `location` fica
//       "denied" para sempre e o refresh silencioso nunca rodava (cidade travada).
//     • `checkPermissions()` / `requestPermissions()` do plugin REJEITAM quando os
//       serviços de localização do aparelho estão desligados (OS-PLUG-GLOC-0007) —
//       o app engolia o erro e o botão ficava "Detectando..." para sempre.
//     • No iOS o `getCurrentPosition` nativo NÃO tem timeout: sem fix de GPS ele
//       nunca resolve nem rejeita.
//     • O `import()` dinâmico do plugin também podia pendurar (chunk não carregado).
//
// Solução: TODA chamada nativa tem timeout nosso, a permissão é resolvida uma única
// vez (e a precisão pedida ao plugin respeita o que foi concedido, evitando re-prompt
// infinito) e há cascata de fallback:
//    plugin.getCurrentPosition → plugin.watchPosition (1º fix) → navigator.geolocation
// Todo estágio é registrado em `getGeoDiagnostics()` para depuração no device.

import { isNative, getPlatform } from "./native";

const PLUGIN_LOAD_TIMEOUT = 8000;
const CHECK_PERM_TIMEOUT = 6000;
const REQUEST_PERM_TIMEOUT = 60000; // depende do usuário responder o diálogo do SO

let _plugin = null;
let _pluginLoadTried = false;
let _diag = { stages: [] };

export class GeoError extends Error {
  constructor(message, code, cause) {
    super(message);
    this.name = "GeoError";
    this.code = code || "GEO_UNKNOWN";
    this.cause = cause;
  }
}

function _stage(name, extra = {}) {
  _diag.stages.push({ name, ...extra, at: new Date().toISOString() });
  if (_diag.stages.length > 24) _diag.stages.shift();
}

function _describe(e) {
  if (!e) return "";
  const code = e.code !== undefined && e.code !== null ? String(e.code) : "";
  const msg = e.message || String(e);
  return code ? `${msg} [${code}]` : msg;
}

/** Estado do último fluxo de geolocalização (para telas de diagnóstico). */
export function getGeoDiagnostics() {
  return JSON.parse(JSON.stringify(_diag));
}

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(
      () => reject(new GeoError(`Tempo esgotado em ${label}`, `TIMEOUT_${label}`)),
      ms,
    );
    Promise.resolve(promise).then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

async function loadNativeGeo() {
  if (_plugin) return _plugin;
  // V18.9.14 — 1º tenta o PROXY NATIVO já registrado pela bridge do Capacitor.
  // Isso NÃO depende do chunk JS do plugin (o `import()` dinâmico abaixo criava um
  // chunk separado que, se falhasse ao carregar no WebView, fazia o app cair
  // silenciosamente no `navigator.geolocation` — que no WKWebView do iOS não pede
  // permissão nenhuma e nunca chama o callback. Era o "nada acontece" do iOS).
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const available = cap && typeof cap.isPluginAvailable === "function"
      ? cap.isPluginAvailable("Geolocation")
      : !!(cap && cap.Plugins && cap.Plugins.Geolocation);
    if (available && cap.Plugins && cap.Plugins.Geolocation) {
      _plugin = cap.Plugins.Geolocation;
      _pluginLoadTried = true;
      _stage("plugin-from-bridge");
      return _plugin;
    }
  } catch (e) {
    _stage("plugin-bridge-fail", { error: _describe(e) });
  }
  if (_pluginLoadTried) return _plugin;
  _pluginLoadTried = true;
  try {
    const mod = await withTimeout(import("@capacitor/geolocation"), PLUGIN_LOAD_TIMEOUT, "plugin-load");
    _plugin = mod?.Geolocation || null;
    _stage("plugin-from-module");
  } catch (e) {
    _stage("plugin-load-fail", { error: _describe(e) });
    _plugin = null;
  }
  return _plugin;
}

/** Diagnóstico: o plugin NATIVO de geolocalização está realmente no build? */
function nativePluginRegistered() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap) return false;
    if (typeof cap.isPluginAvailable === "function") return !!cap.isPluginAvailable("Geolocation");
    if (Array.isArray(cap.PluginHeaders)) return cap.PluginHeaders.some((h) => h && h.name === "Geolocation");
    return !!(cap.Plugins && cap.Plugins.Geolocation);
  } catch {
    return false;
  }
}

const _isDisabledError = (e) => {
  const s = `${e?.code || ""} ${e?.message || ""}`.toLowerCase();
  return s.includes("gloc-0007") || s.includes("not enabled") || s.includes("disabled");
};
const _isDeniedError = (e) => {
  const s = `${e?.code || ""} ${e?.message || ""}`.toLowerCase();
  return s.includes("gloc-0003") || s.includes("denied") || s.includes("restricted");
};

/**
 * Resolve a permissão nativa uma única vez e devolve a precisão concedida.
 * @returns {Promise<{fine:boolean, coarse:boolean}>}
 */
async function ensureNativePermission(Geolocation) {
  const read = (s) => ({
    fine: s?.location === "granted",
    coarse: s?.coarseLocation === "granted" || s?.location === "granted",
  });

  let status = null;
  let checkErr = null;
  try {
    status = await withTimeout(Geolocation.checkPermissions(), CHECK_PERM_TIMEOUT, "checkPermissions");
    _stage("checkPermissions", { location: status?.location, coarse: status?.coarseLocation });
  } catch (e) {
    checkErr = e;
    _stage("checkPermissions-fail", { error: _describe(e) });
    // O plugin rejeita checkPermissions quando o GPS do aparelho está DESLIGADO.
    if (_isDisabledError(e)) {
      throw new GeoError(
        "A localização do aparelho está desligada. Ative o GPS nas configurações e tente de novo.",
        "LOCATION_DISABLED",
        e,
      );
    }
  }

  let perms = read(status);
  if (perms.coarse) return perms;

  try {
    status = await withTimeout(Geolocation.requestPermissions(), REQUEST_PERM_TIMEOUT, "requestPermissions");
    _stage("requestPermissions", { location: status?.location, coarse: status?.coarseLocation });
  } catch (e) {
    _stage("requestPermissions-fail", { error: _describe(e) });
    if (_isDisabledError(e)) {
      throw new GeoError(
        "A localização do aparelho está desligada. Ative o GPS nas configurações e tente de novo.",
        "LOCATION_DISABLED",
        e,
      );
    }
    throw new GeoError(
      "Não foi possível confirmar a permissão de localização.",
      "PERMISSION_ERROR",
      e || checkErr,
    );
  }

  perms = read(status);
  if (!perms.coarse) {
    throw new GeoError(
      "Permissão de localização negada. Libere o acesso à localização nas configurações do AlugaJá.",
      "PERMISSION_DENIED",
    );
  }
  return perms;
}

function nativeWatchOnce(Geolocation, ms, highAccuracy) {
  return new Promise((resolve, reject) => {
    let watchId = null;
    let done = false;
    const cleanup = () => {
      if (watchId) {
        try { Geolocation.clearWatch({ id: watchId }); } catch { /* noop */ }
        watchId = null;
      }
    };
    const finish = (ok, payload) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      cleanup();
      ok ? resolve(payload) : reject(payload);
    };
    const timer = setTimeout(
      () => finish(false, new GeoError("Tempo esgotado em watchPosition", "TIMEOUT_watchPosition")),
      ms,
    );
    try {
      Geolocation.watchPosition(
        { enableHighAccuracy: highAccuracy, timeout: ms, maximumAge: 0 },
        (pos, err) => {
          if (err) { finish(false, err); return; }
          if (pos && pos.coords) finish(true, pos);
        },
      )
        .then((id) => { watchId = id; if (done) cleanup(); })
        .catch((e) => finish(false, e));
    } catch (e) {
      finish(false, e);
    }
  });
}

function getWebPosition(options) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new GeoError("Geolocalização indisponível neste dispositivo.", "UNSUPPORTED"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

/**
 * Retorna a posição atual do dispositivo (mesmo shape do navigator.geolocation).
 * @param {object} [opts]
 * @param {boolean} [opts.enableHighAccuracy=false]
 * @param {number}  [opts.timeout=10000]
 * @param {number}  [opts.maximumAge=60000]
 * @returns {Promise<GeolocationPosition>}
 */
export async function getGeoPosition(opts = {}) {
  const options = {
    enableHighAccuracy: !!opts.enableHighAccuracy,
    timeout: typeof opts.timeout === "number" ? opts.timeout : 10000,
    maximumAge: typeof opts.maximumAge === "number" ? opts.maximumAge : 60000,
  };

  _diag = { platform: getPlatform(), isNative: isNative(), startedAt: new Date().toISOString(), stages: [] };

  if (isNative()) {
    _diag.nativePluginRegistered = nativePluginRegistered();
    const Geolocation = await loadNativeGeo();
    _diag.pluginLoaded = !!Geolocation;
    if (Geolocation) {
      // Permissão: erros "duros" (GPS desligado / negado) param aqui com mensagem clara.
      // Qualquer outra falha (timeout do diálogo, plugin inconsistente) NÃO aborta:
      // seguimos para as tentativas de posição e, por fim, para o fallback web.
      let perms = { fine: false, coarse: false };
      try {
        perms = await ensureNativePermission(Geolocation);
        _diag.permission = perms.fine ? "precisa" : "aproximada";
      } catch (e) {
        _diag.permission = e?.code || "erro";
        if (e?.code === "LOCATION_DISABLED" || e?.code === "PERMISSION_DENIED") throw e;
        _stage("permission-soft-fail", { error: _describe(e) });
      }
      // Só pede alta precisão se a permissão FINE foi concedida — caso contrário o
      // plugin re-exibe o diálogo do sistema em cada chamada (spam de permissão).
      const highAccuracy = perms.fine;

      let pluginUnresponsive = false;
      try {
        const pos = await withTimeout(
          Geolocation.getCurrentPosition({ ...options, enableHighAccuracy: highAccuracy }),
          options.timeout + 4000,
          "getCurrentPosition",
        );
        _stage("native-getCurrentPosition-ok", { accuracy: pos?.coords?.accuracy });
        return pos;
      } catch (e) {
        _stage("native-getCurrentPosition-fail", { error: _describe(e) });
        if (_isDisabledError(e)) {
          throw new GeoError(
            "A localização do aparelho está desligada. Ative o GPS nas configurações e tente de novo.",
            "LOCATION_DISABLED", e,
          );
        }
        // Se o plugin nem respondeu (timeout NOSSO), não vale insistir no watchPosition:
        // vamos direto para o fallback do WebView em vez de gastar mais 15s.
        pluginUnresponsive = String(e?.code || "").startsWith("TIMEOUT_");
      }

      // Fallback 1: watchPosition costuma entregar o primeiro fix quando o
      // getCurrentPosition do plugin falha (iOS sem timeout / Android sem fix recente).
      if (!pluginUnresponsive) {
        try {
          const pos = await nativeWatchOnce(Geolocation, Math.max(options.timeout, 15000), highAccuracy);
          _stage("native-watchPosition-ok", { accuracy: pos?.coords?.accuracy });
          return pos;
        } catch (e) {
          _stage("native-watchPosition-fail", { error: _describe(e) });
        }
      }
    }
  }

  // Fallback 2 (e caminho único no navegador): geolocalização do próprio WebView.
  try {
    const pos = await getWebPosition({ ...options, timeout: Math.max(options.timeout, 12000) });
    _stage("web-ok", { accuracy: pos?.coords?.accuracy });
    return pos;
  } catch (e) {
    _stage("web-fail", { error: _describe(e) });
    if (e instanceof GeoError) throw e;
    if (e?.code === 1) {
      throw new GeoError(
        "Permissão de localização negada. Libere o acesso à localização nas configurações do AlugaJá.",
        "PERMISSION_DENIED", e,
      );
    }
    throw new GeoError(
      "Não conseguimos obter o sinal de GPS. Vá para um local aberto (ou perto de uma janela) e tente novamente.",
      e?.code === 3 ? "POSITION_TIMEOUT" : "POSITION_UNAVAILABLE",
      e,
    );
  }
}

/** Mensagem pronta em pt-BR para exibir ao usuário a partir de um erro do getGeoPosition. */
export function geoErrorMessage(err) {
  if (err instanceof GeoError) return err.message;
  if (err?.code === 1) return "Permissão de localização negada. Libere o acesso nas configurações do AlugaJá.";
  if (err?.code === 2) return "Localização indisponível. Verifique se o GPS está ligado.";
  if (err?.code === 3) return "Tempo esgotado ao buscar o GPS. Tente novamente em local aberto.";
  return "Não foi possível obter sua localização. Verifique o GPS e tente novamente.";
}

/**
 * Adapta `getGeoPosition` para o formato tradicional (success, error) de callbacks.
 */
export function getGeoPositionCb(success, error, opts = {}) {
  getGeoPosition(opts).then((pos) => {
    try { success && success(pos); } catch (e) { console.error(e); }
  }).catch((err) => {
    try { error && error(err); } catch (e) { console.error(e); }
  });
}

/**
 * Verifica o estado atual de permissão sem disparar prompt (quando possível).
 * @returns {Promise<"granted"|"denied"|"prompt"|"unknown">}
 */
export async function checkGeoPermission() {
  if (isNative()) {
    const Geolocation = await loadNativeGeo();
    if (Geolocation) {
      try {
        const s = await withTimeout(Geolocation.checkPermissions(), CHECK_PERM_TIMEOUT, "checkPermissions");
        // IMPORTANTE: no Android 12+ a permissão APROXIMADA deixa `location` = "denied"
        // e só `coarseLocation` = "granted". Antes líamos `s.location || s.coarseLocation`,
        // o que devolvia "denied" e matava o refresh silencioso (cidade travava no default).
        if (s?.location === "granted" || s?.coarseLocation === "granted") return "granted";
        const v = s?.location || s?.coarseLocation || "unknown";
        if (v === "prompt-with-rationale") return "prompt";
        return v;
      } catch (e) {
        return _isDeniedError(e) ? "denied" : "unknown";
      }
    }
  }
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const s = await navigator.permissions.query({ name: "geolocation" });
      return s.state || "unknown";
    }
  } catch {
    /* noop */
  }
  return "unknown";
}
